import { isFamilyName, isGivenName } from "./phi-names";

/** Spoken PHI is replaced with this token in the live transcript. */
export const PHI_HIDDEN = "[hidden]";

const SKIP_NAME = new Set(["you", "speaker", "participant", "customer"]);

const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const URL = /\b(?:https?:\/\/|www\.)\S+/gi;
const IP = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const SSN_SPACED = /\b\d{3}\s\d{2}\s\d{4}\b/g;
const LONG_ID = /\b\d{9}\b/g;
const PHONE = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]\d{3}[\s.-]\d{4}\b/g;
const CARD = /\b\d{4}[\s-]\d{4}[\s-]\d{4}[\s-]\d{4}\b/g;
const PO_BOX = /\bP\.?\s*O\.?\s*Box\s+\d+\b/gi;
const STREET =
  /\b\d{1,6}\s+(?:[A-Za-z0-9.'-]+\s+){0,4}(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Drive|Dr|Lane|Ln|Court|Ct|Way|Place|Pl|Terrace|Ter)(?:\s+(?:Apt|Apartment|Suite|Unit|#)\s*[A-Za-z0-9-]+)?\b\.?/gi;
const STATES =
  "AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC";
const STATE_NAMES =
  "alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming|district of columbia";
const ZIP_WITH_STATE = new RegExp(
  `\\b(?:${STATES})\\s+\\d{5}(?:-\\d{4})?\\b|\\b\\d{5}(?:-\\d{4})?\\s+(?:${STATES})\\b`,
  "g"
);
const MONTH =
  "january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec";
const SPOKEN_DAY =
  "first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|twenty first|twenty second|twenty third|twenty fourth|twenty fifth|twenty sixth|twenty seventh|twenty eighth|twenty ninth|thirtieth|thirty first";
const SPOKEN_ONES = "one|two|three|four|five|six|seven|eight|nine";
const SPOKEN_YEAR = `nineteen(?:\\s+(?:hundred(?:\\s+and)?|oh|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:\\s+(?:${SPOKEN_ONES}))?)?|two thousand(?:\\s+(?:and\\s+)?(?:${SPOKEN_ONES}|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty(?:\\s+(?:${SPOKEN_ONES}))?))?`;
const SPOKEN_DATE = new RegExp(
  `\\b(?:${MONTH})\\s+(?:\\d{1,2}(?:st|nd|rd|th)?|${SPOKEN_DAY})(?:,)?(?:\\s+(?:\\d{4}|${SPOKEN_YEAR}))?\\b`,
  "gi"
);
const NUMERIC_DATE = /\b(?:\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2})\b/g;
const AGE = /\b(?:age(?:d)?\s+(?:is\s+|of\s+)?\d{1,3}|\d{1,3}\s+years?(?:\s+of\s+age|\s+old)|\d{1,3}[- ]year[- ]old)\b/gi;
const ZIP = /\b\d{5}(?:-\d{4})?\b/g;
const RECORD_ID =
  /\b((?:member|subscriber|policy|group|account|beneficiary|claim|plan|rx|license|certificate|serial|medical record|mrn|npi|dea|patient id|social security|ssn)(?:\s+(?:id|number|no|#))?(?:\s+is)?)\s+#?[A-Za-z0-9-]*\d[A-Za-z0-9-]{3,}\b/gi;
const STATE_LIST = STATE_NAMES.split("|").sort((a, b) => b.length - a.length);
const CITY_STATE = new RegExp(`\\b([A-Za-z][A-Za-z.'-]{2,})\\s+(${STATE_LIST.join("|")})\\b`, "gi");
const AWS_PII = /\[(?:PII|NAME|ADDRESS|PHONE|EMAIL|SSN|AGE|PIN|DATE|DATE_TIME|CREDIT_DEBIT_NUMBER|CREDIT_DEBIT_CVV|CREDIT_DEBIT_EXPIRY|BANK_ACCOUNT_NUMBER|BANK_ROUTING|PASSPORT_NUMBER|USERNAME|PASSWORD)\]/gi;
const GENDER_VALUE = "male|female|non-binary|nonbinary|intersex|transgender|trans|cisgender|masculine|feminine";
const GENDER_WORD = new RegExp(`\\b(?:${GENDER_VALUE})\\b`, "gi");
const DOB_CUE =
  /\b(date of birth|birth date|birthdate|d\.?\s*o\.?\s*b\.?|dob|birthday|born on|born)\b(\s+(?:is|was|on|in))?\s+((?:(?!\b(?:and|but|she|he|they|who|when|which|so|then|because)\b)[^.!?\n]){2,48})/gi;
const ADDRESS_CUE =
  /\b(address|lives at|living at|resides at|residing at|lives in|living in)\b(\s+is)?\s+((?:(?!\b(?:and|but|she|he|they|who|which|because)\b)[^.!?\n]){3,60})/gi;

/** Words that follow a name cue, or sit in a capitalized pair, but are not a person's name. */
const NAME_STOP = new Set([
  "a", "an", "the", "and", "or", "to", "of", "for", "on", "in", "at", "from", "with", "about",
  "this", "that", "it", "is", "am", "are", "was", "were", "be", "been", "being", "as", "if",
  "here", "there", "fine", "good", "okay", "ok", "yes", "no", "not", "now", "just", "still",
  "calling", "speaking", "talking", "ready", "back", "done", "sure", "sorry", "please",
  "hello", "hi", "hey", "thanks", "thank", "you", "your", "my", "our", "their", "his", "her",
  "patient", "doctor", "office", "case", "status", "meeting", "call", "phone", "number",
  "gender", "sex", "male", "female", "nonbinary", "transgender", "race", "ethnicity",
  "define", "identify", "identified", "available", "diagnosis", "medication", "medicine",
  "needs", "need", "has", "had", "have", "said", "says", "called", "takes", "taking",
  "went", "going", "will", "can", "cannot", "today", "tomorrow", "yesterday",
  "date", "birth", "born", "dob", "years", "old", "zip", "code", "street", "avenue",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december",
  "english", "missing", "info", "information", "speaker", "participant", "customer", "agent",
  "new", "york", "los", "angeles", "san", "francisco",
  "doing", "well", "better", "worse", "stable", "waiting", "pending", "left", "right",
  "next", "last", "home", "work", "time", "week", "month", "year", "day", "days",
  "business", "prior", "auth", "authorization", "pharmacy", "insurance", "payer",
  "coverage", "approved", "denied", "one", "two", "three", "four", "five", "six",
  "seven", "eight", "nine", "ten", "eleven", "twelve", "hundred", "thousand",
  "first", "second", "third", "area", "town", "city",
]);

const HONORIFIC = "(?:mr|mrs|ms|miss|dr|doctor)\\.?";
const NAME_WORD = "[A-Za-z][A-Za-z']+(?:-[A-Za-z][A-Za-z']+)?";
const NAME_CUE = new RegExp(
  `\\b(?:my name is|name is|i am|i'm|this is|named|speaking with|talking to|calling for|referred by|seen by|patient(?:'s)?\\s+name(?:\\s+is)?|patient(?:'s)?\\s+is|${HONORIFIC})\\s+(?:${HONORIFIC}\\s+)?((?:${NAME_WORD})(?:\\s+${NAME_WORD}){0,2})`,
  "gi"
);
const STATE_NAME_PATTERN = new RegExp(`\\b(?:${STATE_NAMES})\\b`, "i");
const STREET_WORD = /\b(?:street|st|avenue|ave|road|rd|boulevard|blvd|drive|lane|court|way|place|terrace|apartment|suite|unit)\b/i;

/**
 * Hide names, addresses, dates of birth, and the other spoken identifiers
 * in a live transcript line. `keepNames` stay visible when a caller passes them.
 */
export function redactPhi(text: string, keepNames: string[] = []): string {
  if (!text) return text;
  const keep = keepTokens(keepNames);
  let out = text;
  out = swap(out, AWS_PII);
  out = swap(out, EMAIL);
  out = swap(out, URL);
  out = swap(out, IP);
  out = swap(out, SSN);
  out = swap(out, SSN_SPACED);
  out = swap(out, LONG_ID);
  out = swap(out, PHONE);
  out = swap(out, CARD);
  out = swap(out, PO_BOX);
  out = swap(out, STREET);
  out = swap(out, ZIP_WITH_STATE);
  out = maskCueValue(out, DOB_CUE, looksLikeDate);
  out = maskCueValue(out, ADDRESS_CUE, looksLikeAddress);
  out = hideCityState(out);
  out = swap(out, SPOKEN_DATE);
  out = swap(out, NUMERIC_DATE);
  out = swap(out, AGE);
  // Keep medication, condition, and dosage words visible so the transcript stays readable.
  // Identity PHI (names, DOB, address, phone, SSN, record ids, gender) is still hidden.
  out = swap(out, ZIP);
  out = hideRecordId(out);
  out = hideGender(out);
  out = replaceSpokenNames(out, keep);
  out = out.replace(/(?:\[hidden\][\s,]*){2,}/g, `${PHI_HIDDEN} `);
  return out.replace(/[ \t]{2,}/g, " ").trim();
}

function swap(text: string, pattern: RegExp): string {
  pattern.lastIndex = 0;
  return text.replace(pattern, PHI_HIDDEN);
}

/** Hide the words after a date-of-birth or address cue, and keep the cue itself. */
function maskCueValue(text: string, pattern: RegExp, accept: (value: string) => boolean): string {
  pattern.lastIndex = 0;
  return text.replace(pattern, (full, _label: string, _bridge: string, value: string) => {
    const spoken = String(value || "");
    const trimmed = spoken.trim();
    if (!trimmed || !accept(trimmed)) return full;
    const trail = /\s$/.test(spoken) ? " " : "";
    const lead = full.slice(0, full.length - spoken.length);
    return `${lead}${PHI_HIDDEN}${trail}`;
  });
}

function looksLikeDate(value: string): boolean {
  return (
    new RegExp(`\\b(?:${MONTH}|\\d|${SPOKEN_DAY.split("|")[0]})\\b`, "i").test(value) ||
    /\d/.test(value) ||
    new RegExp(`\\b(?:${SPOKEN_YEAR})\\b`, "i").test(value) ||
    new RegExp(`\\b(?:${SPOKEN_DAY})\\b`, "i").test(value)
  );
}

function looksLikeAddress(value: string): boolean {
  return /\d/.test(value) || STREET_WORD.test(value) || STATE_NAME_PATTERN.test(value);
}

/** "gender is, uh, male" keeps the cue and hides the value, including filler words. */
function hideGender(text: string): string {
  GENDER_WORD.lastIndex = 0;
  if (/\b(?:gender|sex|patient)\b/i.test(text)) return text.replace(GENDER_WORD, PHI_HIDDEN);
  return text.replace(
    new RegExp(`\\b((?:is|was|as|a|an)(?:\\s*,?\\s*(?:uh|um|er|ah))*)\\s+(${GENDER_VALUE})\\b`, "gi"),
    (_full, lead: string) => `${lead} ${PHI_HIDDEN}`
  );
}

function hideRecordId(text: string): string {
  RECORD_ID.lastIndex = 0;
  return text.replace(RECORD_ID, (_full, label: string) => `${label} ${PHI_HIDDEN}`);
}

function hideCityState(text: string): string {
  CITY_STATE.lastIndex = 0;
  return text.replace(CITY_STATE, (full, city: string) => {
    const key = String(city || "").toLowerCase().replace(/[^a-z]/g, "");
    return NAME_STOP.has(key) ? full : PHI_HIDDEN;
  });
}

function replaceSpokenNames(text: string, keep: Set<string>): string {
  const cue = new RegExp(NAME_CUE.source, "gi");
  let out = text.replace(cue, (full, names: string) => {
    const words = names.split(/\s+/);
    const lead: string[] = [];
    for (const word of words) {
      if (!isNameWord(word)) break;
      lead.push(word);
    }
    if (!lead.length) return full;
    const spoken = full.slice(0, full.length - names.length);
    const leftover = words.slice(lead.length).join(" ");
    const shown = showOrHide(lead, keep);
    return `${spoken}${shown}${leftover ? ` ${leftover}` : ""}`;
  });
  out = out.replace(
    new RegExp(`\\b${NAME_WORD}(?:\\s+${NAME_WORD}){1,2}\\b`, "g"),
    (match) => hideNamePhrase(match, keep)
  );
  return out;
}

function hideNamePhrase(match: string, keep: Set<string>): string {
  const parts = match.split(/\s+/);
  const pieces: string[] = [];
  let run: string[] = [];
  const flush = () => {
    if (!run.length) return;
    pieces.push(hideCleanName(run, keep));
    run = [];
  };
  for (const part of parts) {
    if (NAME_STOP.has(part.toLowerCase().replace(/[^a-z']/g, ""))) {
      flush();
      pieces.push(part);
    } else {
      run.push(part);
    }
  }
  flush();
  return pieces.join(" ");
}

function hideCleanName(parts: string[], keep: Set<string>): string {
  if (parts.length < 2) return parts.join(" ");
  const recognized = isGivenName(parts[0]) && isFamilyName(parts[parts.length - 1]);
  const titled = parts.every((part) => /^[A-Z]/.test(part));
  if (!recognized && !titled) return parts.join(" ");
  if (parts.every((part) => keep.has(part.toLowerCase().replace(/[^a-z']/g, "")))) return parts.join(" ");
  return showOrHide(parts, keep);
}

function showOrHide(words: string[], keep: Set<string>): string {
  const pieces = words.map((word) => (keep.has(word.toLowerCase().replace(/[^a-z']/g, "")) ? word : PHI_HIDDEN));
  return pieces
    .filter((piece, index) => piece !== PHI_HIDDEN || pieces[index - 1] !== PHI_HIDDEN)
    .join(" ");
}

function keepTokens(keepNames: string[]): Set<string> {
  const keep = new Set<string>();
  for (const raw of keepNames) {
    const full = String(raw || "").trim().toLowerCase();
    if (full.length >= 2 && !SKIP_NAME.has(full)) keep.add(full);
    for (const part of String(raw || "").split(/[\s,._-]+/)) {
      const word = part.replace(/[^A-Za-z']/g, "").toLowerCase();
      if (word.length >= 2 && !SKIP_NAME.has(word)) keep.add(word);
    }
  }
  return keep;
}

function isNameWord(word: string): boolean {
  const key = word.toLowerCase().replace(/[^a-z']/g, "");
  return key.length >= 2 && !NAME_STOP.has(key);
}
